import { useState, useMemo, useCallback } from "react";
import TopMenu from "components/InventoryCategories/TopMenu";
import useCategoryMutations from "graphql/useCategoryMutations";
import useSubcategories, { useCategories } from "graphql/useSubcategories";
import AddSubCategoryModal from "components/common/AddSubCategoryModal";
import List from "components/common/ListWithMoreOptions";
import ModalFullScreen from "components/common/ModalFullScreen";
import Modal from "components/common/Modal";
import useItems from "graphql/useItems";
import moment from "moment";
import { Spin } from "antd";
import { usePaginatedData } from "helpers/hooks";
import BottomPageSelectorWithPagination, {
  usePagination,
} from "components/common/BottomPageSelectorWithPagination";
import { useMessaging } from "context/MessagingContext";
import { useTranslation } from "react-i18next";
import AddCategory from "./AddCategory";
import s from "./styles";

function InventoryCategories() {
  const { t } = useTranslation();
  const [addCategoryModalOpen, setAddItemModalOpen] = useState(false);
  const [addSubcategoryModalOpen, setAddSubcategoryModalOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);

  const { sorting, setSorting, search, setSearch, pagination, setPagination } =
    usePagination("name");

  const { deleteCategory } = useCategoryMutations();

  const { items, loading: loading1 } = useItems();

  const { setInfoMessage, setConfirmMessage, setErrorMessage } = useMessaging();

  function changeSearch(v) {
    setSearch(v);
    setPagination({ ...pagination, page: 0 });
  }

  const { subcategories, loading: loading2 } = useSubcategories();
  const { categories, loading: loading3 } = useCategories();
  const mapSubCategoriesAssignedItems = useMemo(
    () =>
      items.reduce((prev, item) => {
        if (item.subcategoryId) {
          if (!prev[item.subcategoryId]) {
            prev[item.subcategoryId] = 0;
          }
          prev[item.subcategoryId] += 1;
        }
        return prev;
      }, {}),
    [items]
  );

  const categoryMap = useMemo(() => {
    const result = {};
    categories.forEach((category) => {
      result[category._id.toString()] = {
        ...category,
        subcategories: [],
      };
    });
    subcategories.forEach((sub) => {
      result[sub.categoryId]?.subcategories.push({
        ...sub,
        assignedItems: mapSubCategoriesAssignedItems[sub._id] || 0,
      });
    });

    Object.values(result).forEach((c) => {
      c.subcategories.sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
      c.assignedItems = c.subcategories.reduce(
        (prev, sub) => prev + sub.assignedItems,
        0
      );
    });

    return result;
  }, [categories, subcategories, mapSubCategoriesAssignedItems]);

  const data = useMemo(() => {
    let categoriesListFiltered = Object.values(categoryMap).filter((c) =>
      c.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
    );
    categoriesListFiltered = categoriesListFiltered.sort((a, b) => {
      if (a[sorting.columnId] < b[sorting.columnId]) return -1 * sorting.order;
      if (a[sorting.columnId] > b[sorting.columnId]) return 1 * sorting.order;
      return 0;
    });

    const categoriesWithSubcategoriesList = categoriesListFiltered.map(
      (category) => {
        return {
          ...category,
          subcategoriesList: category.subcategories.reduce(
            (prev, sub) => (prev ? `${prev}, ${sub.name}` : sub.name),
            ""
          ),
        };
      }
    );
    return categoriesWithSubcategoriesList;
  }, [categoryMap, search, sorting.columnId, sorting.order]);

  const dataToShow = usePaginatedData(data, pagination, sorting, (item) => [
    item.name,
    item.subcategoriesList,
    item.assignedItems,
    moment(item.updateDatetime).format("lll"),
  ]);

  const loading = loading1 || loading2 || loading3;

  const onUpdateSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.InventoryCategories.categoryEdit"),
      info1: t("containers.InventoryCategories.CanFindCategory"),
    });
  }, []);

  const onAddSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.InventoryCategories.newCategory"),
      info1: t("containers.InventoryCategories.CanFindCategory"),
    });
  }, []);

  const onAddSuccessSubCategory = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.InventoryCategories.newSubcategory"),
      info1: t("containers.InventoryCategories.CanFindCategory"),
    });
  }, []);

  const onDeleteRequest = useCallback((category) => {
    setConfirmMessage({
      titleAlert: t("containers.InventoryCategories.deleteCategory"),
      confirm1: `${t("containers.InventoryCategories.confirmDeleteCategory")} ${
        category.element.name
      }?`,
      confirm2: t("containers.InventoryCategories.confirm2DeleteCategory"),
      onConfirm: () =>
        deleteCategory(category.element).catch((e) =>
          setErrorMessage({ error: e })
        ),
    });
  }, []);

  return (
    <s.PageContainer>
      <TopMenu
        changeSearchValue={(v) => changeSearch(v)}
        addCategory={() => setAddItemModalOpen(true)}
        addSubCategory={() => setAddSubcategoryModalOpen(true)}
        loading={loading}
      />
      {!loading && (
        <s.ListContainer>
          <List
            width="1150px"
            handleEdit={(category) => {
              setItemToEdit(category.element._id);
              setAddItemModalOpen(true);
            }}
            handleDeleteWorker={(category) => {
              onDeleteRequest(category);
            }}
            data={dataToShow}
            columns={[
              {
                title: t("containers.InventoryCategories.category"),
                sortable: true,
                columnId: "name",
              },
              {
                title: t("containers.InventoryCategories.subCategory"),
                sortable: true,
                columnId: "subcategoriesList",
              },
              {
                title: t("containers.InventoryCategories.assignedItems"),
                sortable: true,
                columnId: "assignedItems",
              },
              {
                title: t("containers.InventoryCategories.updateDatetime"),
                sortable: true,
                columnId: "updateDatetime",
              },
              {
                title: "",
              },
            ]}
            sorting={sorting}
            changeSorting={(columnId, order) => setSorting({ columnId, order })}
            tableStyle={{ gridTemplateColumns: "18fr 20fr 10fr 20fr 5fr" }}
          />
        </s.ListContainer>
      )}

      {loading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <Spin
            style={{ color: "#000" }}
            size="large"
            tip={t("containers.InventoryCategories.loading")}
          />
        </div>
      )}

      {!loading && (
        <BottomPageSelectorWithPagination
          pagination={pagination}
          setPagination={setPagination}
          length={data.length}
        />
      )}

      {addCategoryModalOpen && (
        <ModalFullScreen>
          <AddCategory
            closeModal={() => {
              setAddItemModalOpen(false);
              setItemToEdit(null);
            }}
            editItem={categoryMap[itemToEdit]}
            onUpdateSuccess={onUpdateSuccess}
            onAddSuccess={onAddSuccess}
          />
        </ModalFullScreen>
      )}
      <Modal
        onCancel={() => {
          setAddSubcategoryModalOpen(false);
        }}
        visible={addSubcategoryModalOpen}
      >
        {addSubcategoryModalOpen && (
          <AddSubCategoryModal
            close={() => {
              setAddSubcategoryModalOpen(false);
            }}
            setNewSubCategory={() => {}}
            categories={data}
            onUpdateSuccess={onUpdateSuccess}
            onAddSuccessSubCategory={onAddSuccessSubCategory}
          />
        )}
      </Modal>
    </s.PageContainer>
  );
}

export default InventoryCategories;
